
import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import OsEntityToolbar from '@/components/core/OsEntityToolbar.vue';
import OsEntityForm from '@/components/core/OsEntityForm.vue';
import {
  FormOfAddress,
  ICampaign,
  IReportContentCategory,
  Recipientgroup,
  ReportAudience
} from '@/models/application';
import { EntityFormFieldType, IEntityFormField } from '@/models/core';
import { AppDirtyUtil } from '@/utils/AppDirtyUtil';
import OsSheetContent from '@/components/core/sheet/OsSheetContent.vue';
import OsToolbar from '@/components/core/OsToolbar.vue';
import OsAssignedReportCategoriesCheckboxView from '@/components/shared/reportcontentcategories/OsAssignedReportCategoriesCheckboxView.vue';

@Component({
  computed: {
    ReportAudience() {
      return ReportAudience;
    }
  },
  components: {
    OsAssignedReportCategoriesCheckboxView,
    OsToolbar,
    OsSheetContent,
    OsEntityForm,
    OsEntityToolbar
  }
})
export default class OsCampaignForm extends Vue {
  @PropSync('appDirtyUtil', { required: true })
  internalAppDirtyUtil!: AppDirtyUtil;
  @PropSync('value', { required: true })
  internalValue!: ICampaign;
  @PropSync('loading', { default: false })
  internalLoading!: boolean;

  @PropSync('removedCategoriesForCompany', {
    default: () => [],
    required: false
  })
  internalRemovedCategoriesForCompany!: IReportContentCategory[];
  @PropSync('addedCategoriesForCompany', { default: () => [], required: false })
  internalAddedCategoriesForCompany!: IReportContentCategory[];
  @PropSync('removedCategoriesForParticipants', {
    default: () => [],
    required: false
  })
  internalRemovedCategoriesForParticipants!: IReportContentCategory[];
  @PropSync('addedCategoriesForParticipants', {
    default: () => [],
    required: false
  })
  internalAddedCategoriesForParticipants!: IReportContentCategory[];

  @Prop({ required: false, default: false })
  isCreateForm!: boolean;
  @Prop({ required: true })
  customerId!: string;
  @Prop({ default: 'Create new Campaign' })
  titleCreateForm!: string;
  @Prop({ default: 'Campaign' })
  titleExistingForm!: string;
  @Prop({ default: 'Title' })
  textTitle!: string;
  @Prop({ default: 'Questionnaire' })
  textQuestionnaire!: string;
  @Prop({ default: 'Which questionnaire?' })
  textToolbarQuestionnaire!: string;
  @Prop({ default: 'Who should receive the result?' })
  textToolbarRecipientGroup!: string;
  @Prop({ default: 'Company & participants' })
  textRecipientgroupAll!: string;
  @Prop({ default: 'Only your company' })
  textRecipientgroupStaff!: string;
  @Prop({ default: 'Only the participants' })
  textRecipientgroupParticipants!: string;
  @Prop({ default: false })
  showCustomParticipantEmailTemplate!: boolean;

  @Prop({ default: false })
  showformOfAddressOption!: boolean;
  @Prop({ default: 'Form of address' })
  textToolbarformOfAddressOption!: string;
  @Prop({ default: 'Formal' })
  textFormOfAddressFormal!: string;
  @Prop({ default: 'Personal' })
  textFormOfAddressPersonal!: string;

  @Prop({ default: 'What content for the company' })
  textHeaderReportCategoriesCompany!: string;
  @Prop({ default: 'What content for the participants' })
  textHeaderReportCategoriesParticipant!: string;

  @Prop({ default: false })
  canEditReportSegments!: boolean;

  mainFieldsAreValid = false;
  questionnaireFieldsAreValid = false;
  recipientFieldsAreValid = false;
  formOfAddressFieldsAreValid = false;

  get mainFields(): IEntityFormField[] {
    let fields: IEntityFormField[] = [
      {
        type: EntityFormFieldType.text,
        label: this.textTitle,
        propName: 'title',
        smCols: 12,
        required: true
      }
    ];

    if (this.showCustomParticipantEmailTemplate) {
      fields.push({
        type: EntityFormFieldType.text,
        label: 'Custom E-Mail Template ID (Mailjet ID)',
        propName: 'participantReportEmailTemplateId',
        smCols: 6,
        required: false
      });
    }

    return fields;
  }
  get questionnaireFields(): IEntityFormField[] {
    return [
      {
        type: EntityFormFieldType.lookup,
        lookupAsRadio: true,
        label: '',
        propName: 'questionnaireId',
        smCols: 12,
        required: true,
        entityName: 'questionnaire',
        readonly: !this.isCreateForm
      }
    ];
  }
  get recipientFields(): IEntityFormField[] {
    return [
      {
        type: EntityFormFieldType.radio,
        label: '',
        propName: 'recipientgroup',
        smCols: 12,
        required: true,
        readonly:
          !this.$global.roles.isAdmin() && !this.$global.roles.isAdmin(),
        items: [
          {
            key: this.textRecipientgroupAll,
            value: Recipientgroup.All
          },
          {
            key: this.textRecipientgroupStaff,
            value: Recipientgroup.Staff
          },
          {
            key: this.textRecipientgroupParticipants,
            value: Recipientgroup.Participants
          }
        ]
      }
    ];
  }

  get formOfAddressFields(): IEntityFormField[] {
    return [
      {
        type: EntityFormFieldType.radio,
        label: '',
        propName: 'formOfAddress',
        smCols: 12,
        required: true,
        items: [
          {
            key: this.textFormOfAddressFormal,
            value: FormOfAddress.Formal
          },
          {
            key: this.textFormOfAddressPersonal,
            value: FormOfAddress.Personal
          }
        ]
      }
    ];
  }

  get isDirty() {
    return AppDirtyUtil.isDirty;
  }

  onSave() {
    this.$emit('onSave');
  }

  onDelete() {
    this.$emit('onDelete');
  }

  onUndo() {
    this.$emit('undoClicked');
  }

  reloadAssignedCategories() {
    (
      this.$refs.assignedReportCategoriesCheckboxViewComp as any
    )?.reloadAssignedCategories();
    (
      this.$refs.assignedReportCategoriesCheckboxViewPar as any
    )?.reloadAssignedCategories();
  }

  mounted() {
    this.internalAppDirtyUtil.init([
      this.internalValue,
      this.internalAddedCategoriesForCompany,
      this.internalRemovedCategoriesForCompany,
      this.internalAddedCategoriesForParticipants,
      this.internalRemovedCategoriesForParticipants
    ]);
  }

  @Watch('internalValue', { deep: true })
  @Watch('internalAddedCategoriesForCompany', { deep: true })
  @Watch('internalRemovedCategoriesForCompany', { deep: true })
  @Watch('internalAddedCategoriesForParticipants', { deep: true })
  @Watch('internalRemovedCategoriesForParticipants', { deep: true })
  onInternalValueChanged() {
    this.internalAppDirtyUtil.update([
      this.internalValue,
      this.internalAddedCategoriesForCompany,
      this.internalRemovedCategoriesForCompany,
      this.internalAddedCategoriesForParticipants,
      this.internalRemovedCategoriesForParticipants
    ]);
  }
}
